/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { CloseOutlined } from '@ant-design/icons'
import { Modal, ModalProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useCallback, useEffect, useMemo } from 'react'
import { useModalScroll } from 'src/hooks/useModalScroll'
import useWindowSize from 'src/hooks/useWindowSize'
import Button from 'src/components/ts-components/Button'
import Text from 'src/components/ts-components/Text'
import { MODAL_TOKENS } from 'src/styles/modalTokens'
import React from 'react'

/**
 * Extended props for BaseModal component
 */
export interface BaseModalProps
  extends Omit<ModalProps, 'open' | 'title' | 'footer'> {
  /** Controls modal visibility */
  isOpen: boolean
  /** Callback when modal should close */
  onClose: () => void
  /** Modal title - can be string or React node */
  title: React.ReactNode
  /** Custom footer actions - if not provided, default OK/Cancel buttons are shown */
  actions?: React.ReactNode
  /** Override mobile width if needed */
  mobileWidth?: string
  /** Override desktop width if needed */
  desktopWidth?: string
  /** Loading state for default actions */
  isLoading?: boolean
  /** Disable OK button in default actions */
  disableOk?: boolean
  /** OK button text in default actions */
  okText?: string
  /** Cancel button text in default actions */
  cancelText?: string
  /** OK button click handler for default actions */
  onOk?: () => void
  /** Remove horizontal padding from modal body - useful for edge-to-edge content like tables */
  removeBodyPaddingX?: boolean
  /** Raise the modal above another BaseModal (e.g. a nested/info modal opened
   *  from within one) so its mask dims the modal beneath it.
   *
   *  `true` (or `1`) is one step up — the common case of a single nested modal.
   *  Pass a HIGHER NUMBER for deeper nesting: each level adds another step, so a
   *  modal opened from within an already-elevated modal must use a bigger level
   *  than its parent, or the two share a z-index and the child's mask dims the
   *  grandparent instead of its parent. */
  elevated?: boolean | number
}

/**
 * Default actions builder for common OK/Cancel pattern
 */
function getDefaultActions({
  isLoading = false,
  onClose,
  onOk,
  disableOk = false,
  okText = 'OK',
  cancelText = 'Cancel',
  isMobile = false,
}: {
  isLoading?: boolean
  onClose: () => void
  onOk?: () => void
  disableOk?: boolean
  okText?: string
  cancelText?: string
  isMobile?: boolean
}) {
  return (
    <div
      css={css`
        display: flex;
        justify-content: flex-end;
        gap: 16px;
        width: ${isMobile ? '100%' : '50%'};
        margin-left: ${isMobile ? '0' : 'auto'};
        @media (max-width: ${MODAL_TOKENS.MOBILE_BREAKPOINT}px) {
          width: 100%;
          flex-direction: column;
        }
      `}
    >
      <Button
        text={cancelText}
        onClick={onClose}
        variant='secondary'
        disabled={isLoading}
      />
      {onOk && (
        <Button
          text={isLoading ? 'Loading...' : okText}
          onClick={onOk}
          variant='primary'
          disabled={isLoading || disableOk}
        />
      )}
    </div>
  )
}

/**
 * Reusable modal styles and classNames
 */
const modalStyles = css`
  .my-modal-body {
  }
  .my-modal-mask {
    backdrop-filter: blur(1px);
  }
  .my-modal-header {
    border-bottom: 1px dotted ${colors.FIERY_RED};
  }
  .my-modal-footer {
    color: ${colors.FIERY_RED};
  }
  .my-modal-content {
    border: 1px solid #333;
  }
`

const classNames = {
  body: modalStyles['my-modal-body'],
  mask: modalStyles['my-modal-mask'],
  header: modalStyles['my-modal-header'],
  footer: modalStyles['my-modal-footer'],
  content: modalStyles['my-modal-content'],
}

const antdModalStyles = {
  header: {
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
    paddingLeft: MODAL_TOKENS.PADDING_HEADER,
    paddingRight: MODAL_TOKENS.PADDING_HEADER,
  },
  body: {
    borderBottom: `1px solid ${MODAL_TOKENS.BORDER_COLOR}`,
    borderTop: `1px solid ${MODAL_TOKENS.BORDER_COLOR}`,
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
  },
  content: {
    backgroundColor: MODAL_TOKENS.BG_SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: MODAL_TOKENS.PADDING_FOOTER,
    paddingLeft: '0',
    paddingRight: '0',
  },
  wrapper: {
    zIndex: MODAL_TOKENS.Z_INDEX_WRAPPER,
  },
  mask: {
    backdropFilter: `blur(${MODAL_TOKENS.MASK_BLUR})`,
    backgroundColor: MODAL_TOKENS.MASK_COLOR,
    zIndex: MODAL_TOKENS.Z_INDEX_MASK,
  },
  footer: {
    paddingLeft: MODAL_TOKENS.PADDING_FOOTER,
    paddingRight: MODAL_TOKENS.PADDING_FOOTER,
  },
}

/**
 * BaseModal - A reusable modal wrapper that consolidates all common modal patterns
 *
 * Features:
 * - Responsive design (mobile-first)
 * - Consistent styling and theming
 * - Automatic scroll locking
 * - Default action buttons
 * - Customizable while maintaining consistency
 *
 * @example
 * ```tsx
 * <BaseModal
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   title="Confirm Action"
 *   onOk={handleConfirm}
 *   isLoading={isLoading}
 * >
 *   <p>Are you sure you want to proceed?</p>
 * </BaseModal>
 * ```
 */
const BaseModal: React.FC<BaseModalProps> = ({
  isOpen,
  onClose,
  title,
  actions,
  children,
  mobileWidth,
  desktopWidth,
  isLoading = false,
  disableOk = false,
  okText = 'OK',
  cancelText = 'Cancel',
  onOk,
  removeBodyPaddingX = false,
  elevated = false,
  ...modalProps
}) => {
  const { width } = useWindowSize()
  // When elevated (a modal opened from within another modal), raise the wrapper
  // + mask above the standard modal layer so the mask dims the modal beneath.
  // The level multiplies the step, so each nesting depth gets its own layer:
  // a level-2 modal's mask lands above a level-1 modal's wrapper.
  const elevationLevel = elevated === true ? 1 : Number(elevated) || 0
  const resolvedStyles = elevationLevel
    ? {
        ...antdModalStyles,
        wrapper: {
          ...antdModalStyles.wrapper,
          zIndex:
            MODAL_TOKENS.Z_INDEX_WRAPPER +
            MODAL_TOKENS.Z_INDEX_ELEVATION_STEP * elevationLevel,
        },
        mask: {
          ...antdModalStyles.mask,
          zIndex:
            MODAL_TOKENS.Z_INDEX_MASK +
            MODAL_TOKENS.Z_INDEX_ELEVATION_STEP * elevationLevel,
        },
      }
    : antdModalStyles
  const isMobile = useMemo(
    () => width <= MODAL_TOKENS.MOBILE_BREAKPOINT,
    [width]
  )
  const { openModal, closeModal } = useModalScroll()

  // Auto-manage scroll locking. No unmount cleanup needed here — `useModalScroll`
  // releases its own lock when it unmounts, which is what keeps the page
  // scrollable for callers that render this as `{isOpen && <BaseModal/>}` or
  // `if (!isOpen) return null` and so unmount it while still open.
  useEffect(() => {
    if (!isOpen) {
      return
    }
    openModal()
    return () => closeModal()
  }, [closeModal, isOpen, openModal])

  // Default close icon
  const closeIcon = useCallback(() => {
    return (
      <CloseOutlined
        css={css`
          color: ${colors.SOLITUDE};
          font-size: 18px;
          cursor: pointer;
          transition: all 0.2s;
          &:hover {
            color: ${colors.WHITE};
          }
        `}
      />
    )
  }, [])

  // Header component
  const getHeader = useCallback(() => {
    if (typeof title === 'string') {
      return (
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {title}
        </Text>
      )
    }
    return title
  }, [title])

  // Footer component
  const getFooter = useCallback(() => {
    if (actions) {
      return actions
    }

    return getDefaultActions({
      isLoading,
      onClose,
      onOk,
      disableOk,
      okText,
      cancelText,
      isMobile,
    })
  }, [
    actions,
    isLoading,
    onClose,
    onOk,
    disableOk,
    okText,
    cancelText,
    isMobile,
  ])

  const modalWidth = isMobile
    ? mobileWidth || MODAL_TOKENS.WIDTH_MOBILE
    : desktopWidth || MODAL_TOKENS.WIDTH_DESKTOP

  return (
    <Modal
      css={css`
        display: flex;
        flex: 1;
        height: ${isMobile
          ? MODAL_TOKENS.HEIGHT_MOBILE
          : MODAL_TOKENS.HEIGHT_DESKTOP} !important;
        margin: ${isMobile
          ? MODAL_TOKENS.MARGIN_MOBILE
          : MODAL_TOKENS.MARGIN_DESKTOP};
        padding: 0;
        max-width: ${isMobile
          ? MODAL_TOKENS.WIDTH_MOBILE
          : desktopWidth || MODAL_TOKENS.WIDTH_DESKTOP};
        top: ${isMobile ? '0' : '50%'};
        /* SN-8793: on desktop the dialog stops at the window and the body
           scrolls, so the title and the actions stay on screen. Uncapped, antd's
           wrapper scrolled the whole dialog instead. */
        .ant-modal-content {
          height: ${isMobile
            ? MODAL_TOKENS.HEIGHT_MOBILE
            : MODAL_TOKENS.HEIGHT_DESKTOP} !important;
          max-height: ${isMobile ? 'none' : MODAL_TOKENS.MAX_HEIGHT_DESKTOP};
          border-radius: ${isMobile
            ? MODAL_TOKENS.BORDER_RADIUS_MOBILE
            : MODAL_TOKENS.BORDER_RADIUS}px;
          display: flex;
          flex-direction: column;
        }
        .ant-modal-body {
          flex: 1;
          overflow-x: ${isMobile ? 'auto' : 'hidden'};
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: ${MODAL_TOKENS.BORDER_COLOR} transparent;
          display: flex;
          flex-direction: column;
          padding: ${removeBodyPaddingX
            ? `${MODAL_TOKENS.PADDING_BODY} 0`
            : MODAL_TOKENS.PADDING_BODY};
          background: ${MODAL_TOKENS.BG_SECONDARY_300};
        }
      `}
      width={modalWidth}
      closeIcon={closeIcon()}
      title={getHeader()}
      footer={getFooter()}
      open={isOpen}
      onCancel={onClose}
      centered={!isMobile}
      maskClosable={false}
      classNames={classNames}
      styles={resolvedStyles}
      {...modalProps}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: ${removeBodyPaddingX ? '0' : '0 8px'};
        `}
      >
        {children}
      </div>
    </Modal>
  )
}

export default BaseModal
